import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { saudeService } from '../../services/saude.service';
import PageContainer from '../../components/PageContainer';
import PageHeader from '../../components/PageHeader';
import { useToast } from '../../components/Toast';
import { formatCPF } from '../../utils/formatters';
import DecisaoAbonoModal from '../../components/Saude/DecisaoAbonoModal';
import TimelineSaudeModal from '../../components/Saude/TimelineSaudeModal';
import ModalIndividuoFichaClinica from '../../components/Saude/ModalIndividuoFichaClinica';
import RadarRiscosChart from '../../components/Saude/RadarRiscosChart';
import {
  HeartIcon,
  SparklesIcon,
  UserGroupIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  BuildingOfficeIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ArrowRightIcon,
  FunnelIcon,
  ChartBarIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import type { ColaboradorAtestadoContract } from '@sstexames/shared-types';

export default function SaudeCorporativaDashboardPage() {
  const { empresa } = useEmpresa();
  const toast = useToast();
  const empresaId = empresa?.id || 0;
  const [activeTab, setActiveTab] = useState<'empresa' | 'setores' | 'pessoas'>('empresa');

  // Modais de Ação para as Pessoas
  const [atestadoParaAbono, setAtestadoParaAbono] = useState<ColaboradorAtestadoContract | null>(null);
  const [colaboradorTimeline, setColaboradorTimeline] = useState<{ id: number; nome: string } | null>(null);
  const [colaboradorFichaClinicaId, setColaboradorFichaClinicaId] = useState<number | null>(null);

  // Filtros da aba de Pessoas
  const [filtroStatusAdmin, setFiltroStatusAdmin] = useState<string>('');

  // 1. Dados Executivos do Dashboard
  const { data: dashboard } = useQuery({
    queryKey: ['saude-dashboard-empresa', empresaId],
    queryFn: () => saudeService.obterDashboardSaudeEmpresa(empresaId),
    enabled: !!empresaId,
  });

  // 2. Detalhes Estratégicos com Setores e 5W2H
  const { data: detalhes } = useQuery({
    queryKey: ['saude-detalhes-empresa', empresaId],
    queryFn: () => saudeService.obterEmpresaDetalhes(empresaId),
    enabled: !!empresaId,
  });

  // 3. Lista Nominal de Colaboradores e Atestados para a Visão "Pessoas"
  const { data: atestados = [], refetch: refetchAtestados } = useQuery({
    queryKey: ['saude-pessoas-atestados', empresaId, filtroStatusAdmin],
    queryFn: () =>
      saudeService.listarAtestados({
        empresa_id: empresaId,
        status_administracao: filtroStatusAdmin || undefined,
      }),
    enabled: !!empresaId,
  });

  const renderBadgeAdmin = (status: string) => {
    switch (status) {
      case 'ABONO_ACEITO':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <CheckCircleIcon className="w-3.5 h-3.5" />
            Abonado
          </span>
        );
      case 'ABONO_RECUSADO':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">
            <XCircleIcon className="w-3.5 h-3.5" />
            Recusado
          </span>
        );
      case 'EM_ANALISE':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
            <ClockIcon className="w-3.5 h-3.5" />
            Em Análise
          </span>
        );
    }
  };

  const variacoesExames = dashboard?.variacoes_achados_exames || detalhes?.variacoes_achados_exames || [
    {
      exame_nome: 'Exame Clínico Ocupacional / ASO',
      tipo: 'CLINICO' as const,
      total_realizados: dashboard?.total_colaboradores_ativos || 340,
      total_com_achado: dashboard?.total_vidas_com_achados || 42,
      taxa_alteracao: 12.4,
      classificacao_gravidade: 'MODERADA' as const,
      significado_clinico: 'Alterações pressóricas (PAS >= 140 mmHg) e índice de massa corporal elevado.',
      impacto_ocupacional: 'Atenção redobrada para trabalhos em altura, espaços confinados e operação de maquinário pesado.',
    },
    {
      exame_nome: 'Audiometria Tonal e Vocal',
      tipo: 'COMPLEMENTAR' as const,
      total_realizados: Math.round((dashboard?.total_colaboradores_ativos || 340) * 0.55),
      total_com_achado: 18,
      taxa_alteracao: 9.7,
      classificacao_gravidade: 'MODERADA' as const,
      significado_clinico: 'Rebaixamento neurossensorial em frequências agudas (3kHz a 6kHz) compatível com ruído.',
      impacto_ocupacional: 'Reforço imediato na fiscalização e ensaio de vedação de protetores auditivos tipo concha.',
    },
    {
      exame_nome: 'Anamnese: Dores Osteomusculares & Coluna',
      tipo: 'ANAMNESE' as const,
      total_realizados: dashboard?.total_colaboradores_ativos || 340,
      total_com_achado: 64,
      taxa_alteracao: 18.8,
      classificacao_gravidade: 'SEVERA' as const,
      significado_clinico: 'Queixas crônicas de lombalgia mecânica, sobrecarga articular e tensão cervical.',
      impacto_ocupacional: 'Principal causa de atestados médicos e risco de concausalidade em afastamentos INSS B91.',
    },
  ];

  const demografiaCruzada = dashboard?.demografia_cruzada || detalhes?.demografia_cruzada;
  const sugestoesSegmentadas = detalhes?.sugestoes_segmentadas || dashboard?.sugestoes_segmentadas;

  return (
    <PageContainer>
      <PageHeader
        title="Painel Estratégico de Saúde Corporativa"
        description="Monitoramento populacional integrado em 3 dimensões: Gestão Executiva da Empresa, Diagnóstico Preventivo dos Setores e Acompanhamento Longitudinal das Pessoas."
        breadcrumbs={[
          { label: 'Saúde Corporativa', href: '/saude/dashboard' },
          { label: 'Painel Estratégico' },
        ]}
      />

      <div className="space-y-6 mt-4">
        {/* Navegação Tripartite Estratégica: Empresa / Setores / Pessoas */}
        <div className="flex border-b border-gray-200 gap-2 overflow-x-auto pb-px">
          <button
            type="button"
            onClick={() => setActiveTab('empresa')}
            className={`flex items-center gap-2 py-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'empresa'
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-lg'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BuildingOfficeIcon className="w-4 h-4" />
            1. A Empresa (Visão Executiva & Epidemiologia)
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('setores')}
            className={`flex items-center gap-2 py-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'setores'
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-lg'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <SparklesIcon className="w-4 h-4" />
            2. Os Setores (Fatores de Risco & Planos 5W2H)
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('pessoas')}
            className={`flex items-center gap-2 py-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'pessoas'
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-lg'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <UserIcon className="w-4 h-4" />
            3. As Pessoas (Casos Clínicos & Absenteísmo)
          </button>
        </div>

        {/* ========================================================================= */}
        {/* ABA 1: A EMPRESA (VISÃO EXECUTIVA & EPIDEMIOLOGIA) */}
        {/* ========================================================================= */}
        {activeTab === 'empresa' && (
          <div className="space-y-6">
            {/* Banner de Risco & Alerta Previdenciário (NTEP / FAP / INSS) */}
            {dashboard?.casos_potenciais_inss && dashboard.casos_potenciais_inss > 0 ? (
              <div className="bg-gradient-to-r from-amber-500/10 via-rose-500/10 to-amber-500/5 border border-amber-300 rounded-xl p-4 flex items-center justify-between shadow-xs">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-500 text-white rounded-lg shadow-xs">
                    <ExclamationTriangleIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">
                      Gatilho Previdenciário (INSS): {dashboard.casos_potenciais_inss} colaborador(es) com risco de afastamento superior a 15 dias
                    </h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Colaboradores acumularam jornadas consecutivas pelo mesmo CID-10. Recomendamos avaliação médica pericial prévia para mitigação de NTEP e FAP.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTab('pessoas')}
                  className="text-xs font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 px-3.5 py-1.5 rounded-lg border border-amber-300 transition-colors flex items-center gap-1 shrink-0"
                >
                  Ver Casos Afetados <ArrowRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : null}

            {/* 4 Cards Principais com Scores de Saúde Geral e Risco Ocupacional */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Score de Saúde Geral */}
              <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs">
                <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                  <span>Score Saúde Geral</span>
                  <HeartIcon className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-extrabold text-emerald-900 font-mono">
                    {dashboard?.score_saude_geral ?? 84}
                  </span>
                  <span className="text-xs text-emerald-700 font-semibold">/100</span>
                </div>
                <p className="text-[11px] text-emerald-700 mt-1">
                  {dashboard?.total_vidas_sem_achados ?? 0} vidas sem nenhum achado
                </p>
              </div>

              {/* Score Médio de Risco Ocupacional */}
              <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs">
                <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                  <span>Risco Ocupacional Médio</span>
                  <ArrowTrendingUpIcon className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="mt-2 text-2xl font-bold text-indigo-900 font-mono">
                  {dashboard?.score_medio_risco_ocupacional ?? 5.8}%
                </div>
                <p className="text-[11px] text-indigo-600 mt-1">
                  Probabilidade média de afastamento
                </p>
              </div>

              {/* Colaboradores Ativos */}
              <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs">
                <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                  <span>População Monitorada</span>
                  <UserGroupIcon className="w-5 h-5 text-indigo-500" />
                </div>
                <div className="mt-2 text-2xl font-bold text-gray-900">
                  {dashboard?.total_colaboradores_ativos ?? 0}
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  {dashboard?.total_vidas_com_achados ?? 0} com achados monitorados
                </p>
              </div>

              {/* Taxa de Absenteísmo */}
              <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-xs">
                <div className="flex items-center justify-between text-gray-500 text-xs font-semibold uppercase tracking-wider">
                  <span>Taxa de Absenteísmo</span>
                  <ClockIcon className="w-5 h-5 text-rose-500" />
                </div>
                <div className="mt-2 text-2xl font-bold text-rose-700">
                  {dashboard?.taxa_absenteismo_percentual ?? 0}%
                </div>
                <p className="text-[11px] text-rose-600 mt-1">
                  {dashboard?.total_dias_afastamento ?? 0} dias de afastamento perdidos
                </p>
              </div>
            </div>

            {/* Painel Central: Gráfico de Aranha (Radar de Riscos) + Variações e Achados em Exames */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* Radar de Riscos Ocupacionais (Gráfico de Aranha NR-01) */}
                <div className="lg:col-span-5 p-5 bg-slate-50 rounded-2xl border border-slate-200 flex flex-col items-center">
                  <div className="w-full flex items-center justify-between pb-3 border-b border-slate-200 mb-2">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5 uppercase tracking-wider">
                      <SparklesIcon className="w-4 h-4 text-indigo-600" />
                      Radar de Riscos Ocupacionais (NR-01)
                    </span>
                    <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
                      6 Grupos Canônicos
                    </span>
                  </div>

                  <RadarRiscosChart dados={dashboard?.radar_riscos || detalhes?.radar_riscos} tamanho={310} />
                </div>

                {/* Tabela de Variações e Achados nos Exames Clínicos e Complementares */}
                <div className="lg:col-span-7 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1.5">
                      <ChartBarIcon className="w-4 h-4 text-indigo-600" />
                      Principais Variações & Achados em Exames e Anamneses
                    </h4>
                    <span className="text-[11px] text-gray-500">
                      Tradução médica e impacto operacional
                    </span>
                  </div>

                  <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1">
                    {variacoesExames.map((item, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-slate-50 hover:bg-slate-100/80 rounded-xl border border-slate-200 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-bold text-gray-900">
                            {item.exame_nome}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-mono font-bold text-indigo-900 bg-indigo-100 px-2 py-0.5 rounded">
                              {item.taxa_alteracao}% alterados ({item.total_com_achado}/{item.total_realizados})
                            </span>
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                                item.classificacao_gravidade === 'SEVERA'
                                  ? 'bg-red-100 text-red-800'
                                  : item.classificacao_gravidade === 'MODERADA'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {item.classificacao_gravidade}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-700 mt-1.5 leading-snug">
                          <strong>Significado Clínico:</strong> {item.significado_clinico}
                        </p>
                        <p className="text-[11px] text-emerald-800 bg-emerald-50/80 rounded px-2 py-1 mt-1 border border-emerald-100">
                          <strong>Impacto Ocupacional:</strong> {item.impacto_ocupacional}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Análise Demográfica Cruzada: Faixa Etária x Sexo x Risco Relativo */}
            {demografiaCruzada && demografiaCruzada.faixa_etaria_sexo?.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <UserGroupIcon className="w-5 h-5 text-indigo-600" />
                    Análise Demográfica Cruzada: Faixa Etária × Sexo × Risco Relativo (RR)
                  </h3>
                  <span className="text-xs text-gray-500">
                    Cruzamento de vulnerabilidade populacional para medicina preventiva
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 text-[11px] font-bold text-gray-500 uppercase tracking-wider bg-gray-50">
                        <th className="py-2.5 px-4">Faixa Etária</th>
                        <th className="py-2.5 px-4 text-center">Masc. com Achados</th>
                        <th className="py-2.5 px-4 text-center">Masc. Saudáveis</th>
                        <th className="py-2.5 px-4 text-center">Fem. com Achados</th>
                        <th className="py-2.5 px-4 text-center">Fem. Saudáveis</th>
                        <th className="py-2.5 px-4 text-center">Risco Relativo (RR)</th>
                        <th className="py-2.5 px-4">Vigilância Recomendada</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {demografiaCruzada.faixa_etaria_sexo.map((f, i) => (
                        <tr key={i} className="hover:bg-gray-50/80 transition-colors">
                          <td className="py-2.5 px-4 font-bold text-gray-900">
                            {f.faixa}
                          </td>
                          <td className="py-2.5 px-4 text-center font-mono text-red-600 font-semibold">
                            {f.masculino_com_achado}
                          </td>
                          <td className="py-2.5 px-4 text-center font-mono text-emerald-700">
                            {f.masculino_sem_achado}
                          </td>
                          <td className="py-2.5 px-4 text-center font-mono text-red-600 font-semibold">
                            {f.feminino_com_achado}
                          </td>
                          <td className="py-2.5 px-4 text-center font-mono text-emerald-700">
                            {f.feminino_sem_achado}
                          </td>
                          <td className="py-2.5 px-4 text-center">
                            <span
                              className={`font-mono font-bold px-2 py-0.5 rounded text-[11px] ${
                                f.taxa_risco_relativo >= 1.5
                                  ? 'bg-red-100 text-red-800'
                                  : f.taxa_risco_relativo >= 1.0
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-emerald-100 text-emerald-800'
                              }`}
                            >
                              {f.taxa_risco_relativo}x média
                            </span>
                          </td>
                          <td className="py-2.5 px-4 text-gray-600 text-[11px]">
                            {f.taxa_risco_relativo >= 1.3
                              ? 'Rastreamento cardiometabólico e pausas ativas reforçadas'
                              : 'Monitoramento preventivo anual padrão do PCMSO'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Painel Epidemiológico: CIDs e Impacto por Setor */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top CIDs */}
              <div className="bg-white border border-gray-200/80 rounded-xl p-5 shadow-xs">
                <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                  <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <HeartIcon className="w-5 h-5 text-indigo-600" />
                    Patologias & CIDs Mais Frequentes
                  </h4>
                  <span className="text-[11px] text-gray-400">Classificação CID-10</span>
                </div>

                <div className="mt-4 space-y-3">
                  {dashboard?.cids_mais_frequentes && dashboard.cids_mais_frequentes.length > 0 ? (
                    dashboard.cids_mais_frequentes.map((item, idx) => (
                      <div
                        key={item.cid}
                        className="p-3 rounded-lg border border-gray-100 bg-gray-50/50 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-indigo-800 text-xs">
                                {item.cid}
                              </span>
                              <span className="text-xs text-gray-700 font-medium">
                                {item.descricao || 'Patologia geral'}
                              </span>
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                              {item.total_ocorrencias} atestado(s)
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-xs font-bold text-amber-700">
                            {item.total_dias_perdidos} dias perdidos
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400 py-6 text-center">
                      Nenhuma patologia recorrente mapeada no período.
                    </p>
                  )}
                </div>
              </div>

              {/* Panorama Rápido dos Setores */}
              <div className="bg-white border border-gray-200/80 rounded-xl p-5 shadow-xs">
                <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                  <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <BuildingOfficeIcon className="w-5 h-5 text-teal-600" />
                    Impacto por Setor da Empresa
                  </h4>
                  <button
                    type="button"
                    onClick={() => setActiveTab('setores')}
                    className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
                  >
                    Ver detalhes completos →
                  </button>
                </div>

                <div className="mt-4 space-y-3">
                  {dashboard?.distribuicao_por_setor && dashboard.distribuicao_por_setor.length > 0 ? (
                    dashboard.distribuicao_por_setor.map((setor) => (
                      <div
                        key={setor.setor_nome}
                        className="p-3 rounded-lg border border-gray-100 bg-gray-50/50 flex items-center justify-between"
                      >
                        <div>
                          <h5 className="text-xs font-bold text-gray-900">
                            {setor.setor_nome}
                          </h5>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            {setor.total_atestados} atestado(s) submetido(s)
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded">
                            {setor.dias_afastamento} dias
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400 py-6 text-center">
                      Nenhum setor com afastamento registrado no período.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* ABA 2: OS SETORES (FATORES DE RISCO & PLANOS DE AÇÃO 5W2H) */}
        {/* ========================================================================= */}
        {activeTab === 'setores' && (
          <div className="space-y-6">
            {/* Tabela Comparativa de Setores */}
            <div className="bg-white border border-gray-200/80 rounded-xl overflow-hidden shadow-xs">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">
                    Ranking de Setores & Criticidade Operacional
                  </h3>
                  <p className="text-xs text-gray-500">
                    Correlação entre afastamentos médicos e fatores de riscos ocupacionais (PGR / PCMSO).
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-50/80 text-gray-600 font-semibold border-b border-gray-200">
                    <tr>
                      <th className="py-3 px-4">Setor</th>
                      <th className="py-3 px-4">População Monitorada</th>
                      <th className="py-3 px-4">Taxa de Absenteísmo</th>
                      <th className="py-3 px-4">Ações Preventivas</th>
                      <th className="py-3 px-4">Riscos Mapeados (PGR)</th>
                      <th className="py-3 px-4">Criticidade</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700">
                    {detalhes?.setores_risco && detalhes.setores_risco.length > 0 ? (
                      detalhes.setores_risco.map((st) => (
                        <tr key={st.setor_id} className="hover:bg-gray-50/60 transition-colors">
                          <td className="py-3 px-4 font-bold text-gray-900">
                            {st.setor_nome}
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {st.total_vidas} vidas
                          </td>
                          <td className="py-3 px-4 font-semibold text-rose-600">
                            {st.taxa_absenteismo}%
                          </td>
                          <td className="py-3 px-4 font-medium text-indigo-700">
                            {st.total_acoes_preventivas} plano(s)
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {st.principais_riscos && st.principais_riscos.length > 0 ? (
                                st.principais_riscos.map((r, i) => (
                                  <span
                                    key={i}
                                    className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                  >
                                    {r}
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-400 italic">Geral / Não específico</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                                st.taxa_absenteismo > 3.0
                                  ? 'bg-rose-100 text-rose-800 border border-rose-300'
                                  : st.taxa_absenteismo > 1.5
                                  ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                  : 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                              }`}
                            >
                              {st.taxa_absenteismo > 3.0
                                ? 'Crítica'
                                : st.taxa_absenteismo > 1.5
                                ? 'Moderada'
                                : 'Controlada'}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-gray-400">
                          Nenhum setor com afastamentos mapeados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Planos 5W2H Hiper-Segmentados por Setor, Cargo e Faixa Etária */}
            <div className="bg-white border border-gray-200/80 rounded-xl p-6 shadow-xs space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 flex-wrap gap-2">
                <div className="flex items-center gap-2 text-indigo-900 font-bold text-sm">
                  <LightBulbIcon className="w-5 h-5 text-indigo-600" />
                  <h4>Planos de Ação Hiper-Segmentados (5W2H Sustentados por Evidências)</h4>
                </div>
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200">
                  Segmentação Específica por Setor &amp; Cargo
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(sugestoesSegmentadas || [
                  {
                    id: 'sug-cust-1',
                    alvo_tipo: 'SETOR' as const,
                    alvo_nome: 'Logística & Almoxarifado',
                    populacao_afetada: 45,
                    prioridade: 'CRITICA' as const,
                    titulo: 'Adequação Biomecânica de Docas & Pegas',
                    justificativa_motivo: 'Setor concentra maior absenteísmo por lombalgia mecânica (CID M54.5).',
                    recomendacao_5w2h: {
                      what: 'Implementar esteiras reguláveis e pausas ergonômicas de 10 min a cada 2h',
                      why: 'Eliminar concausalidade em DORT e reduzir afastamentos B91',
                      where: 'Docas de carregamento 1 a 4',
                      who: 'Engenharia de Segurança & Fisioterapia do Trabalho',
                      when: 'Próximos 20 dias úteis',
                      how: 'Ajuste de esteiras e rodízio programado de colaboradores',
                      kpi_alvo: 'Redução de 40% nos atestados de coluna em 90 dias',
                    },
                  },
                  {
                    id: 'sug-cust-2',
                    alvo_tipo: 'GRUPO_CARGO' as const,
                    alvo_nome: 'Operador de Máquinas / Prensas',
                    populacao_afetada: 32,
                    prioridade: 'ALTA' as const,
                    titulo: 'Auditoria de Atenuação Auditiva (Fit Testing)',
                    justificativa_motivo: 'Limiar auditivo limítrofe identificado na audiometria periódica.',
                    recomendacao_5w2h: {
                      what: 'Teste prático de vedação de protetores auriculares tipo concha',
                      why: 'Garantir nível de ruído efetivo abaixo de 80 dBA',
                      where: 'Postos de prensagem e corte',
                      who: 'Fonoaudiologia ocupacional credenciada',
                      when: 'Próxima semana, em 2 turmas',
                      how: 'Demonstração de ajuste e teste de ruído com decibelímetro',
                      kpi_alvo: '100% de operadores com vedação auditiva adequada',
                    },
                  },
                  {
                    id: 'sug-cust-3',
                    alvo_tipo: 'FAIXA_ETARIA' as const,
                    alvo_nome: 'Colaboradores 46 a 55 anos',
                    populacao_afetada: 58,
                    prioridade: 'ALTA' as const,
                    titulo: 'Campanha de Rastreamento Cardiometabólico',
                    justificativa_motivo: 'Faixa etária com maior risco relativo de alteração pressórica no ASO.',
                    recomendacao_5w2h: {
                      what: 'Blitz voluntária de aferição de PA e glicemia capilar',
                      why: 'Prevenção primária de eventos cardiovasculares agudos',
                      where: 'Ambulatório interno',
                      who: 'Enfermagem do Trabalho e Médico do PCMSO',
                      when: 'Durante o próximo mês de campanhas',
                      how: 'Atendimento rápido individual de 10 min por ordem de chegada',
                      kpi_alvo: 'Atingir 85% de cobertura do público da faixa etária',
                    },
                  },
                ]).map((sug) => (
                  <div
                    key={sug.id}
                    className="p-4 rounded-xl border border-indigo-100 bg-gradient-to-b from-indigo-50/40 to-white flex flex-col justify-between space-y-3 shadow-xs"
                  >
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 uppercase tracking-wider">
                          {sug.alvo_tipo}: {sug.alvo_nome}
                        </span>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                            sug.prioridade === 'CRITICA'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {sug.prioridade}
                        </span>
                      </div>

                      <h5 className="text-xs font-bold text-gray-900 mt-2">
                        {sug.titulo}
                      </h5>

                      <p className="text-[11px] text-gray-500 mt-1 italic">
                        Motivo: {sug.justificativa_motivo}
                      </p>

                      <div className="mt-2.5 pt-2 border-t border-indigo-100/80 space-y-1 text-xs text-gray-700 leading-snug">
                        <p><strong>O que:</strong> {sug.recomendacao_5w2h.what}</p>
                        <p><strong>Por que:</strong> {sug.recomendacao_5w2h.why}</p>
                        <p><strong>Onde:</strong> {sug.recomendacao_5w2h.where}</p>
                        <p><strong>Quem:</strong> {sug.recomendacao_5w2h.who}</p>
                        <p><strong>Quando:</strong> {sug.recomendacao_5w2h.when}</p>
                        <p><strong>Como:</strong> {sug.recomendacao_5w2h.how}</p>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-indigo-100 text-[11px] font-bold text-indigo-900 bg-indigo-50/70 p-2 rounded-lg">
                      🎯 KPI Alvo: {sug.recomendacao_5w2h.kpi_alvo}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* ABA 3: AS PESSOAS (CASOS CLÍNICOS & ABSENTEÍSMO NOMINAL) */}
        {/* ========================================================================= */}
        {activeTab === 'pessoas' && (
          <div className="space-y-6">
            {/* Header de Filtros da Aba de Pessoas */}
            <div className="bg-white border border-gray-200/80 rounded-xl p-4 shadow-xs flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-sm font-bold text-gray-900">
                  Acompanhamento Nominal de Colaboradores Afastados
                </h3>
                <p className="text-xs text-gray-500">
                  Visão individual respeitando sigilo e LGPD, com acesso à ficha clínica 360°, mapa anatômico e deferimento de abonos.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <FunnelIcon className="w-4 h-4 text-gray-400" />
                  <span>Filtrar Abono:</span>
                </div>
                <select
                  value={filtroStatusAdmin}
                  onChange={(e) => setFiltroStatusAdmin(e.target.value)}
                  className="text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Todos os Status</option>
                  <option value="EM_ANALISE">Pendente RH</option>
                  <option value="ABONO_ACEITO">Abono Aprovado</option>
                  <option value="ABONO_RECUSADO">Abono Recusado</option>
                </select>

                <Link
                  to="/saude/atestados"
                  className="text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                >
                  Central de Atestados →
                </Link>
              </div>
            </div>

            {/* Tabela de Colaboradores e Afastamentos */}
            <div className="bg-white border border-gray-200/80 rounded-xl overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-50/80 text-gray-600 font-semibold border-b border-gray-200">
                    <tr>
                      <th className="py-3 px-4">Colaborador</th>
                      <th className="py-3 px-4">Início</th>
                      <th className="py-3 px-4">Término</th>
                      <th className="py-3 px-4">Dias</th>
                      <th className="py-3 px-4">CID Informado</th>
                      <th className="py-3 px-4">Médico Emissor</th>
                      <th className="py-3 px-4">Status Abono</th>
                      <th className="py-3 px-4 text-center">Ações Clínicas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700">
                    {atestados.length > 0 ? (
                      atestados.map((atestado) => {
                        const isCriticoInss = atestado.dias_afastamento > 15;
                        return (
                          <tr
                            key={atestado.id}
                            className={`hover:bg-gray-50/60 transition-colors ${
                              isCriticoInss ? 'bg-amber-50/30' : ''
                            }`}
                          >
                            <td className="py-3 px-4">
                              <Link
                                to={`/colaboradores/${atestado.colaborador_id}?aba=atestados`}
                                className="font-bold text-gray-900 hover:text-indigo-600 transition-colors block"
                              >
                                {atestado.colaborador_nome || `Colaborador #${atestado.colaborador_id}`}
                              </Link>
                              <span className="text-[11px] text-gray-500 font-mono">
                                CPF: {formatCPF(atestado.colaborador_cpf || '')}
                              </span>
                            </td>
                            <td className="py-3 px-4 font-medium">
                              {atestado.data_inicio
                                ? new Date(atestado.data_inicio).toLocaleDateString('pt-BR')
                                : '-'}
                            </td>
                            <td className="py-3 px-4 font-medium">
                              {atestado.data_fim
                                ? new Date(atestado.data_fim).toLocaleDateString('pt-BR')
                                : '-'}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`font-bold px-2 py-0.5 rounded ${
                                  isCriticoInss
                                    ? 'bg-rose-100 text-rose-800 border border-rose-200'
                                    : 'text-gray-900'
                                }`}
                              >
                                {atestado.dias_afastamento} dias
                              </span>
                              {isCriticoInss && (
                                <span className="block text-[10px] text-rose-600 font-bold mt-0.5">
                                  Gatilho INSS
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {atestado.motivo_cid ? (
                                <span className="font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">
                                  {atestado.motivo_cid}
                                </span>
                              ) : (
                                <span className="text-gray-400 italic">Não informado</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-medium text-gray-800 block">
                                {atestado.medico_nome || 'Médico não extraído'}
                              </span>
                              {atestado.medico_crm && (
                                <span className="text-[10px] text-gray-500 font-mono">
                                  CRM: {atestado.medico_crm} - {atestado.medico_uf}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {renderBadgeAdmin(atestado.status_administracao)}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => setColaboradorFichaClinicaId(atestado.colaborador_id)}
                                  title="Ver Mapa Anatômico 360° e Ficha Clínica do Colaborador"
                                  className="p-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                                >
                                  <SparklesIcon className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setColaboradorTimeline({
                                      id: atestado.colaborador_id,
                                      nome: atestado.colaborador_nome || 'Colaborador',
                                    })
                                  }
                                  title="Ver Linha do Tempo Longitudinal de Saúde"
                                  className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                </button>
                                {atestado.status_administracao === 'EM_ANALISE' && (
                                  <button
                                    type="button"
                                    onClick={() => setAtestadoParaAbono(atestado)}
                                    title="Decidir Abono RH"
                                    className="p-1.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
                                  >
                                    <ClockIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-gray-400">
                          Nenhum afastamento localizado com os filtros atuais.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Decisão de Abono */}
      {atestadoParaAbono && (
        <DecisaoAbonoModal
          atestado={atestadoParaAbono}
          isOpen={!!atestadoParaAbono}
          toast={toast}
          onClose={() => setAtestadoParaAbono(null)}
          onSuccess={() => {
            refetchAtestados();
            setAtestadoParaAbono(null);
          }}
        />
      )}

      {/* Modal de Linha do Tempo Unificada de Saúde */}
      {colaboradorTimeline && (
        <TimelineSaudeModal
          isOpen={!!colaboradorTimeline}
          colaboradorId={colaboradorTimeline.id}
          colaboradorNome={colaboradorTimeline.nome}
          onClose={() => setColaboradorTimeline(null)}
        />
      )}

      {/* Modal de Ficha Clínica 360° e Mapa Anatômico Corporal */}
      {colaboradorFichaClinicaId !== null && (
        <ModalIndividuoFichaClinica
          isOpen={colaboradorFichaClinicaId !== null}
          colaboradorId={colaboradorFichaClinicaId}
          onClose={() => setColaboradorFichaClinicaId(null)}
        />
      )}
    </PageContainer>
  );
}
